import { useEffect, useState } from "react";
import { T } from "../../../app/tokens.js";
import { getProjectWeek, patchWeeklyFinancials } from "../../../data/projectWeeklyClient.js";
import { useEditablePanel } from "../../../hooks/useEditablePanel.js";
import { FinancialAmountFields, financialDraft, financialPatch } from "./FinancialAmountFields.jsx";
import { FinancialHistory } from "./FinancialHistory.jsx";

export function ProjectFinancialEditor({ project, onSaved, onEditorState }) {
  const [access, setAccess] = useState(null), [error, setError] = useState(null), [busy, setBusy] = useState(false), [revision, setRevision] = useState(0);
  const financials = { budget: project.approvedBudget, forecast: project.forecastAtCompletion, spendToDate: project.spentToDate,
    actualsThrough: project.actualsThrough || project.spentActuals?.asOfDate };
  const panel = useEditablePanel(() => financialDraft(financials));
  const reset = panel.reset;
  useEffect(() => {
    let active = true;
    // Omission requests the server's current week rather than a client-clock guess.
    getProjectWeek(project.id).then(value => { if (active) setAccess(value); }).catch(e => { if (active) setError(e.message); });
    return () => { active = false; };
  }, [project.id]);
  const allowed = access?.capabilities?.canFinancials && !access.frozen && project.financialsTracked !== false;
  useEffect(() => {
    onEditorState?.({ editing: panel.editing, busy });
    return () => onEditorState?.({ editing: false, busy: false });
  }, [panel.editing, busy, onEditorState]);
  useEffect(() => { if (!allowed) reset(); }, [allowed, reset]);
  async function save() {
    setError(null);
    let patch;
    try { patch = financialPatch(panel.draft, financials); } catch (e) { setError(e.message); return; }
    if (!Object.keys(patch).length) { panel.reset(); return; }
    setBusy(true);
    try {
      await patchWeeklyFinancials(project.id, { weekDate: access.weekDate, ...patch });
      panel.reset(); setRevision(n => n + 1); onSaved?.();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }
  return <section aria-label="Project financial amounts" style={{ marginBottom: 16 }}>
    {allowed && !panel.editing && <button type="button" style={{ color: T.blue }} onClick={() => { setError(null); panel.setEditing(true); }}>Edit project financials</button>}
    {allowed && panel.editing && <div>
      <FinancialAmountFields draft={panel.draft} financials={financials} setField={panel.setField} busy={busy} />
      <div style={{ marginTop: 8 }}><button type="button" disabled={busy} onClick={save}>Save</button>{" "}
        <button type="button" disabled={busy} onClick={() => { panel.reset(); setError(null); }}>Cancel</button></div>
    </div>}
    {error && <div role="alert" style={{ color: T.red }}>{error}</div>}
    <FinancialHistory projectId={project.id} revision={revision} />
  </section>;
}
